<template>
  <nx-fullscreen-dialog
    :active="active"
    :title="$t('message.wizard.new.' + this.type)"
    @dismiss="dismiss">

    <template slot="toolbar-items">
      <slot name="toolbar" :validForm="validForm">
         <v-btn
           v-if="step === '2'"
           @click="nextStep(false)"
           color="accent"
           dark flat
           :loading="loading"
           :disabled="createButtonDisabled"
         >{{ $t('message.wizard.createAndContinueBtn') }}
         </v-btn>
        <v-btn
          @click="nextStep"
          color="accent"
          dark flat
          :loading="loading"
          :disabled="createButtonDisabled"
        >{{ nextStepText }}
        </v-btn>
      </slot>
    </template>

    <v-container py-3 fluid class="nx-content-wizard-body">
      <v-layout wrap fill-height>
        <v-flex class="lg6 offset-lg3 md8 offset-md2 sm10 offset-sm1 xs12">

          <nx-form class="nx-form" @form="flags = $event" ref="form">

            <v-stepper v-model="step" color="info" class="elevation-0">
              <v-stepper-header class="elevation-0">
                <v-stepper-step
                  color="info"
                  step="1"
                  :complete="step > 1"
                  editable
                >
                  Datos básicos
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step
                  color="info"
                  step="2"
                  :complete="step2Complete"
                >
                  Imagen de apertura
                </v-stepper-step>
              </v-stepper-header>

              <v-stepper-items>
                <v-stepper-content step="1">

                  <nx-form-text
                    :label="$t('message.form.fields.title')"
                    name="title"
                    v-model="draft.title"
                    validation="required|min:3"
                    :disabled="loading"
                  ></nx-form-text>

                  <nx-taxonomy-form-selector
                    :auth="taxonomyAuth"
                    :label="$t('message.wizard.category')"
                    :limit="1"
                    :type="categoryTypes"
                    :additional-filters="{archived: 'no'}"
                    @authorized="onTaxonomyAuthorized"
                    @input="onTaxonomyInput"
                    name="mainTaxonomyId"
                    v-model="draft.mainTaxonomyId"
                    validation="required"
                    :disabled="loading"
                  ></nx-taxonomy-form-selector>

                  <v-select
                    :items="videoProviders"
                    :label="$t('message.wizard.videoEmbed.type.label')"
                    v-model="provider"
                    :disabled="loading"
                  ></v-select>

                  <nx-form-text
                    :hint="$t(`message.wizard.videoEmbed.${provider}.hint`)"
                    :label="$t('message.form.fields.videoEmbedUrl')"
                    :placeholder="$t(`message.wizard.videoEmbed.${provider}.placeholder`)"
                    name="videoEmbedUrl"
                    persistent-hint
                    v-if="provider !== 'embed'"
                    v-model="draft.videoEmbedUrl"
                    :validation="videoEmbedUrlValidation"
                    :disabled="loading"
                  ></nx-form-text>

                  <nx-textarea
                    :disabled="loading"
                    :label="$t('message.form.fields.videoEmbedString')"
                    auto-grow
                    name="videoEmbedString"
                    v-else
                    v-model="draft.videoEmbedString"
                    :validation="videoEmbedStringRequired ? 'required' : null"
                  ></nx-textarea>
                </v-stepper-content>

                <v-stepper-content step="2">
                  <nx-relation
                    :label="$t('message.form.fields.relationImage')"
                    :limit="1"
                    v-model="draft.relationImage"
                    name="relationImage"
                    relationType="relationImage"
                    :validation="relationImageValidation"
                  ></nx-relation>
                </v-stepper-content>
              </v-stepper-items>

            </v-stepper>
          </nx-form>
        </v-flex>
      </v-layout>
    </v-container>

  </nx-fullscreen-dialog>
</template>

<script>
import Wizard from '@/modules/content/wizard/ContentWizard';
import config from '@/modules/content/wizard/config/videoEmbed';
import Form from '@/modules/form/Form';
import relationApi from '@/modules/relation/api';
import api from './api';

export default {
  name: 'nx-content-videoEmbed-wizard',
  extends: Wizard,
  inject: {
    $getParentEditor: {
      default: null,
    },
  },
  components: {
    [Form.name]: Form,
  },
  props: {
    type: {
      type: String,
      default: 'videoembed',
    },
  },
  data() {
    return {
      draft: {
        title: '',
        mainTaxonomyId: null,
        videoEmbedUrl: '',
        relationImage: null,
      },
      loading: false,
      provider: config.defaultVideoProvider,
      videoEmbedStringRequired: config.videoEmbedStringRequired,
      step: '1',
      navigateToEntityEditor: true,
    };
  },
  computed: {
    nextStepText() {
      if (this.step === '2') {
        return this.$t('message.wizard.createAndEditBtn', { type: this.type });
      }
      return this.$t('message.wizard.nextBtn');
    },
    createButtonDisabled() {
      if (this.loading) return true;
      return !this.flags.valid;
    },
    providerConfig() {
      return config.videoProviders[this.provider] || null;
    },
    providerOembedHandler() {
      return this.providerConfig
        ? this.providerConfig.handler
        : (() => false);
    },
    providerRegex() {
      return this.providerConfig
        ? this.providerConfig.regex
        : '';
    },
    step2Complete() {
      return this.step === '2' && !this.createButtonDisabled;
    },
    validForm() {
      return this.step === '1'
        ? this.flags.valid
        : this.flags.valid && this.draft.relationImage;
    },
    videoEmbedUrlValidation() {
      const validation = { required: true };
      if (this.providerRegex) {
        validation.regex = this.providerRegex;
      }
      return validation;
    },
    videoProviders() {
      return ['embed']
        .concat(config.videoProvidersAvailable)
        .map(provider => ({
          value: provider,
          text: this.$t(`message.wizard.videoEmbed.type.${provider}`),
        }));
    },
    relationImageValidation() {
      return (this.step === '2') ? 'required' : '';
    },
    categoryTypes() {
      return config.wizardCategoryTypes;
    },
  },
  methods: {
    entityCreated(entity) {
      this.$emit('entityCreated', entity);
    },
    async getOembed() {
      if (!this.providerConfig) {
        return;
      }
      this.loading = true;
      const oembed = await this.providerOembedHandler(this.draft.videoEmbedUrl);
      if (oembed === false
        || !oembed.thumbnail_url) {
        this.$delete(this.draft, 'videoEmbedString'); // Remove property so it doesn't get send to the server as a empty string
        this.loading = false;
        return;
      }

      const openingImage = await api.createContentImageFromUrl(oembed.thumbnail_url, {
        context: this.context,
        mainTaxonomyId: this.draft.mainTaxonomyId,
        autopublish: true,
        title: oembed.title,
      });
      if (!openingImage || !openingImage.id) {
        this.loading = false;
        return;
      }

      const relation = await relationApi.getFields({
        entityIds: [openingImage.id],
        relationType: 'relationImage',
      }, this.$getParentEditor ? this.$getParentEditor().id : null);
      if (!relation || !Array.isArray(relation) || relation.length === 0) {
        this.loading = false;
        return;
      }

      this.draft.relationImage = relation[0] || null;
      this.loading = false;
    },
    async nextStep(navigateToEntityEditor = true) {
      this.navigateToEntityEditor = navigateToEntityEditor !== false;
      if (this.step === '1') {
        await this.getOembed();
        this.step = '2';
      } else {
        this.submit();
      }
    },
    onDismiss() {
      this.$emit('dismiss');
    },
    onLoad(state) {
      this.loading = state;
    },
    onSuccess(file) {
      this.$refs.wizard.success(file.response.response);
      this.loading = false;
    },
  },
  watch: {
    provider(newValue) {
      if (newValue === 'embed') {
        this.$delete(this.draft, 'videoEmbedUrl');
        return;
      }
      this.$delete(this.draft, 'videoEmbedString');
      if (this.draft.videoEmbedUrl) {
        this.validateForm();
      }
    },
    active() {
      if (!this.active) {
        this.step = '1';
        this.draft = {
          title: '',
          mainTaxonomyId: null,
          videoEmbedUrl: '',
          relationImage: null,
        };
      }
    },
  },
};
</script>
